import { Dispatch, SetStateAction, useContext, useEffect, useMemo } from 'react';

import { Flex } from '@chakra-ui/react';

import { Customer, CustomerContactTypeEnum } from '../../../gql/customerGql';
import { Deal } from '../../../gql/dealGql';
import {
  DealContact,
  DealUnreadMessageCountDocument,
  DealUnreadMessageCountQuery,
  DealUnreadMessageCountQueryVariables,
  Maybe,
  Notification,
  OriginalLessee,
  useDealUnreadMessageCountLazyQuery,
  useMarkRecipientThreadSeenMutation,
  useNotificationsSeenMutation,
} from '../../../gql/generated/graphql';

import ContactBox from './ContactBox';

import {
  PersonIdFieldMapKey,
  RecipientTypeMap,
  isMessageUnseen,
} from '../../../constants/notifications';
import { useUser } from '../../../hooks/useUser';
import { logger } from '../../../libs/Logger';
import { TextWidgetContext } from '../../../libs/contextLib';
import { pluckDefined } from '../../../utils';
import {
  ConversationBox,
  getBoxUnreadCount,
  getConversationBoxes,
  isActiveBox,
} from '../../../utils/messages';

type Recipient = Maybe<Customer | DealContact | OriginalLessee>;

type ContactPanelProps = {
  deal: Deal | null;
  setRecipient: Dispatch<SetStateAction<Recipient>>;
};

const isOrphanNotification = (notification: Notification): boolean =>
  !notification.customer_id && !notification.deal_contact_id && !notification.original_lessee_id;

const ContactPanel = ({ deal, setRecipient }: ContactPanelProps) => {
  const user = useUser();
  const { activeConversation, setActiveConversation } = useContext(TextWidgetContext);
  const [notificationsSeenMutation] = useNotificationsSeenMutation();
  const [markRecipientThreadSeen] = useMarkRecipientThreadSeenMutation();
  const [fetchUnreadCount, { data: unreadCountData }] = useDealUnreadMessageCountLazyQuery();

  const boxes = useMemo(() => (deal ? getConversationBoxes({ deal }) : []), [deal]);
  const recipientByRole: Record<PersonIdFieldMapKey, Recipient | undefined> = {
    [CustomerContactTypeEnum.Customer]: deal?.customer,
    [CustomerContactTypeEnum.Cobuyer]: deal?.cobuyer,
    [CustomerContactTypeEnum.Contact]: deal?.contact,
    [CustomerContactTypeEnum.Second]: deal?.second_contact,
    [CustomerContactTypeEnum.OriginalLessee]: deal?.original_lessee,
  };

  const openBox = (box: ConversationBox): void => {
    setRecipient(recipientByRole[box.role]);
    setActiveConversation(box.conversation);
  };

  useEffect(() => {
    const orphans = (deal?.notifications ?? []).filter(
      (notification) =>
        isOrphanNotification(notification) && isMessageUnseen(user.id, notification),
    );
    if (!deal?.id || !orphans.length) {
      return;
    }
    const markOrphansSeen = async (): Promise<void> => {
      try {
        await notificationsSeenMutation({
          variables: { notificationIds: pluckDefined(orphans, 'id'), seen: true },
        });
      } catch (e) {
        logger.error('ContactPanel.tsx', 'marking texts seen failed', null, e);
      }
    };
    markOrphansSeen();
  }, [user.id, deal?.id, deal?.notifications]);

  useEffect(() => {
    if (!deal?.id) {
      return;
    }
    const dealId = Number(deal.id);
    const activeBox = boxes.find((box) => isActiveBox({ box, activeConversation }));
    const markActiveThreadSeen = async ({
      box,
      personId,
    }: {
      box: ConversationBox;
      personId: number;
    }): Promise<boolean> => {
      try {
        await markRecipientThreadSeen({
          variables: {
            dealId,
            recipientType: RecipientTypeMap[box.role],
            recipientId: personId,
            phoneNumber: box.conversation.phone_number,
          },
          update: (cache, { data }) => {
            if (!data) {
              return;
            }
            cache.writeQuery<DealUnreadMessageCountQuery, DealUnreadMessageCountQueryVariables>({
              query: DealUnreadMessageCountDocument,
              variables: { dealId },
              data: { dealUnreadMessageCount: data.markRecipientThreadSeen },
            });
          },
        });
        return true;
      } catch (e) {
        logger.error('ContactPanel.tsx', 'marking thread seen failed', null, e);
        return false;
      }
    };
    const refreshUnreadCounts = async (): Promise<void> => {
      const isMarked =
        activeBox?.personId != null &&
        (await markActiveThreadSeen({ box: activeBox, personId: activeBox.personId }));
      fetchUnreadCount({
        variables: { dealId },
        fetchPolicy: isMarked ? 'cache-first' : 'network-only',
      });
    };
    refreshUnreadCounts();
  }, [deal?.id, activeConversation.role, activeConversation.phone_number]);

  return (
    <Flex w="100%" minH="60px" bgColor="white" mb={-2} pb={3} overflowX="auto">
      {boxes.map((box) => (
        <ContactBox
          key={`${box.role}-${box.phoneType}`}
          box={box}
          handleClick={() => openBox(box)}
          isActive={isActiveBox({ box, activeConversation })}
          unreadCount={getBoxUnreadCount({
            box,
            byRecipient: unreadCountData?.dealUnreadMessageCount.byRecipient ?? [],
          })}
        />
      ))}
    </Flex>
  );
};

export default ContactPanel;
